import { Field, Form, useField, useFormikContext } from 'formik';
import { ErrorMessageWrapper } from 'ui/forms';
import styled, { css, useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  ButtonReset,
  Dropdown,
  PopoverContainer,
  PopoverItemButton as RawPopoverItemButton,
  PopoverMenu,
  Stack,
} from '@koob/margaret';
import { Text, FormLabel, InlineFormField, Divider, FormField } from 'ui';
import {
  TextField,
  SegmentedControlField,
  UnsavedFormTrigger,
  FormNavigationBlocker,
} from 'components/Fields';
import PeriodsField from 'components/Fields/PeriodsField';
import PromotionsNightsNumberField from 'components/Fields/PromotionsNightsNumberField';
import { useEffect, useState } from 'react';
import VisuallyHidden from '../VisuallyHidden';
import PromotionCalculationOrder from 'components/Promotions/PromotionCalculationOrder';
import { isEmpty } from 'lodash';
import {
  BenefitsKindEnum,
  PromotionKindEnum,
  PromotionStateEnum,
  PromotionTypeEnum,
  StayFlexibleCodeEnum,
} from '@koob/enums';
import { formatDate } from '@koob/dates';
import {
  KoobBadge,
  KoobButton,
  KoobInputCheckboxField,
  KoobInputStepperField,
  KoobInputToggle,
  KoobModal,
  KoobCard,
} from '@koob/ui';
import { Link } from 'react-router-dom';
import { getPromotionExclusiveFamily } from 'utils/promotions';

const Grid = styled.div`
  width: 100%;
  display: grid;
  grid-gap: ${({ theme }) => theme.spacing()};
  grid-template-rows: auto;
  grid-template-areas: ${({ gridTemplateAreas }) =>
    gridTemplateAreas
      ? gridTemplateAreas
      : `'top top'
       'deal period'
       'promotionParams period'
       'cancellationPolicies cancellationPolicies'
      `};
`;

export const Cell = styled(Stack).attrs({
  direction: 'column',
  gutterSize: 0.5,
  size: 'full',
  alignX: 'stretch',
})`
  ${({ gridArea }) =>
    gridArea &&
    `
    grid-area: ${gridArea};
  `}
`;

const TextPadding = styled(Text)`
  padding: ${({ theme }) => theme.spacing(0.25)}
    ${({ theme }) => theme.spacing(2)};
`;

const Wrapper = styled(Stack).attrs({ direction: 'column' })`
  padding: ${({ theme }) => theme.spacing(0.5)} 0;
`;

const DropdownToggleWrapper = styled(Stack)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(0.5)};
  box-shadow: 0 0 4px rgb(0 0 0 / 0.08);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  margin-bottom: ${({ theme }) => theme.spacing(0.25)};
  justify-content: space-between;
  align-items: center;
`;

const DropdownToggle = ({ children }) => {
  return (
    <DropdownToggleWrapper>
      {children}{' '}
      <i
        className="fa-light fa-chevron-down"
        style={{ fontSize: 16 }}
        aria-hidden="true"
      />
    </DropdownToggleWrapper>
  );
};

const PopoverItemButton = styled(RawPopoverItemButton)`
  padding: ${({ theme }) => theme.spacing(0.5)}
    ${({ theme }) => theme.spacing()};

  color: ${({ theme }) => theme.textLight};

  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

const SummaryListHeader = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-self: stretch;
  align-items: center;
  justify-content: space-between;
  margin-block: ${({ theme }) => theme.spacing(1)}
    ${({ theme }) => theme.spacing(0.5)};
`;

const ClearAllButton = styled(ButtonReset).attrs({ type: 'button' })`
  color: ${({ theme }) => theme.secondary};
  text-decoration: underline;

  ${({ empty }) =>
    empty
      ? css`
          color: ${({ theme }) => theme.textLighter};
        `
      : null};
`;

const DisplayListView = styled.ul`
  list-style: none;
  padding: ${({ theme }) => theme.spacing(0.5)}
    ${({ theme }) => theme.spacing(1)};
  margin: 0;
  border: solid 1px rgba(0, 0, 0, 0.12);
  border-radius: ${({ theme }) => theme.borderRadius.default};
  width: 100%;
  overflow-y: auto;
  max-height: 12ch;
`;

const DisplayListItem = styled.li`
  padding: ${({ theme }) => theme.spacing(0.25)} 0;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;
`;

const SelectionLabel = styled.span`
  text-transform: uppercase;
`;

const Deal = ({ displayName, icon }) => {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <i className={`fa-regular ${icon}`} aria-hidden="true" />
      <span className="truncate">{displayName}</span>
    </span>
  );
};

const Deals = {
  basic_deal: 'basic_deal',
  early_booker: 'early_booker',
  last_minute: 'last_minute',
  stay_flexibility: 'stay_flexibility',
};

const isStayFlexibilityKind = (kind) =>
  kind === PromotionKindEnum.STAY_FLEXIBILITY;

const isSupplementPromotionType = (type) =>
  [PromotionTypeEnum.SUPPLEMENT, PromotionTypeEnum.SUPPLEMENT_PERCENT].includes(
    type,
  );

const CumulativePromotionsField = ({
  name,
  promotions,
  disabled,
  hasCalculationOrder = false,
  isDuplicate = false,
}) => {
  const { t } = useTranslation('promotionForm');
  const theme = useTheme();
  const { values, initialValues } = useFormikContext();
  const [{ value }, , { setValue }] = useField(name);
  const [{ value: promotionType }] = useField('params.type');
  const [
    { value: isRequiredSupplement },
    ,
    { setValue: setIsRequiredSupplement },
  ] = useField('mandatorySupplement');
  const supplementType = ['supplement', 'supplement_percent'];
  const isDesabled = supplementType.includes(promotionType?.value);
  const isStayFlexibilitySelected = isStayFlexibilityKind(values?.kind);
  const getFormExclusiveFamily = (formValues) =>
    getPromotionExclusiveFamily({
      kind: formValues?.kind,
      benefitKind:
        BenefitsKindEnum[formValues?.params?.type?.value?.toUpperCase()],
    });
  const exclusiveFamily = getFormExclusiveFamily(values);
  // A duplicate is a new promotion: the cumulation it copies is not a saved one.
  const shouldDropExclusive =
    isDuplicate || exclusiveFamily !== getFormExclusiveFamily(initialValues);
  const isMutuallyExclusive = (promotion) =>
    Boolean(exclusiveFamily) && promotion.exclusiveFamily === exclusiveFamily;
  const selectablePromotions = (promotions ?? []).filter(
    (promotion) => !isMutuallyExclusive(promotion),
  );
  const excludedPromotionIds = (promotions ?? [])
    .filter(isMutuallyExclusive)
    .map(({ id }) => id)
    .join(',');
  const isAllSelected = selectablePromotions.every(({ id }) =>
    value?.some((item) => item.id === id),
  );

  const handleClick = (selected) => {
    if (value?.length > 0) {
      if (value?.some(({ id }) => id === selected.id)) {
        setValue(value.filter(({ id }) => id !== selected.id));
      } else {
        setValue([...value, { ...selected }]);
      }
    } else {
      setValue([{ ...selected }]);
    }
  };

  // Keeps the promotions already selected as they are: they may carry a rank,
  // and a saved one may no longer be selectable.
  const handleSelectAll = () => {
    setValue([
      ...(value ?? []),
      ...selectablePromotions
        .filter(({ id }) => !value?.some((item) => item.id === id))
        .map(({ id, name }) => ({ id, name })),
    ]);
  };

  const handleRemoveAll = () => {
    setValue([]);
  };

  const handleRemoveById = (id) => {
    const nextState = value?.filter((item) => item.id !== id);
    setValue(nextState);
  };

  useEffect(() => {
    if (isStayFlexibilitySelected) {
      setIsRequiredSupplement(false);
    }
  }, [isStayFlexibilitySelected, setIsRequiredSupplement]);

  // Drops the selected promotions made mutually exclusive by a change of name,
  // deal or type. Cumulations saved as such are left alone.
  useEffect(() => {
    const excludedIds = excludedPromotionIds.split(',');
    if (
      !disabled &&
      shouldDropExclusive &&
      value?.some(({ id }) => excludedIds.includes(`${id}`))
    ) {
      setValue(value.filter(({ id }) => !excludedIds.includes(`${id}`)));
    }
  }, [disabled, excludedPromotionIds, shouldDropExclusive, setValue, value]);

  return (
    <Wrapper>
      <FormField>
        {supplementType.includes(promotionType?.value) && (
          <Stack direction="column" className="mb-1">
            <FormLabel>{t('mandatorySupplement')}</FormLabel>
            <KoobInputToggle
              checked={isStayFlexibilitySelected ? false : isRequiredSupplement}
              disabled={isStayFlexibilitySelected}
              onCheckedChange={(checked) => {
                if (isStayFlexibilitySelected) {
                  return;
                }
                setIsRequiredSupplement(checked);
                setValue([]);
              }}
            />
          </Stack>
        )}
        <FormLabel>{t('cumulativePromotions.select')}</FormLabel>
        <Stack direction="column" gutterSize={1} size="full" alignX="stretch">
          {/* disable supplement cumulativity for v1 */}
          <Dropdown
            trigger={
              <div
                className={
                  isDesabled || disabled ? 'bg-gray-200 cursor-not-allowed' : ''
                }
              >
                <DropdownToggle>
                  {hasCalculationOrder && value?.length > 0
                    ? t('cumulativePromotions.selectedCount', {
                        count: value.length,
                      })
                    : t('cumulativePromotions.select')}
                </DropdownToggle>
              </div>
            }
            disabled={isDesabled || disabled}
          >
            <PopoverContainer>
              <PopoverMenu>
                {selectablePromotions.map(({ id, name }) => (
                  <PopoverItemButton
                    key={id}
                    onClick={() => handleClick({ id, name })}
                    type="button"
                  >
                    <Stack
                      size="full"
                      alignX="space-between"
                      alignY="center"
                      gutterSize={0.5}
                    >
                      <Stack alignY="center" gutterSize={0.25}>
                        <Text type="body">
                          <SelectionLabel>{name}</SelectionLabel>
                        </Text>
                      </Stack>
                      {value?.some((bed) => bed.id === id) ? (
                        <i
                          className="fa-regular fa-circle-check"
                          style={{ fontSize: 24, color: theme.primary }}
                        />
                      ) : (
                        <i
                          className="fa-regular fa-circle-plus"
                          style={{ fontSize: 24, color: theme.textLight }}
                        />
                      )}
                    </Stack>
                  </PopoverItemButton>
                ))}
              </PopoverMenu>
            </PopoverContainer>
          </Dropdown>
        </Stack>
      </FormField>
      {hasCalculationOrder ? (
        <>
          {!disabled && (
            <Stack size="full" gutterSize={0.5} alignX="flex-end">
              <ClearAllButton empty={isAllSelected} onClick={handleSelectAll}>
                {t('cumulativePromotions.selectAll')}
              </ClearAllButton>
              <ClearAllButton
                empty={(value?.length ?? 0) === 0}
                onClick={handleRemoveAll}
              >
                {t('cumulativePromotions.deselectAll')}
              </ClearAllButton>
            </Stack>
          )}
          <PromotionCalculationOrder
            name={name}
            currentRankName="cumulationCalculationOrder"
            currentTitle={values?.name}
            promotions={promotions}
            required={values?.state === PromotionStateEnum.OPERATIONAL}
            disabled={disabled}
            onRemove={handleRemoveById}
          />
        </>
      ) : (
        <>
          <SummaryListHeader>
            <span style={{ color: theme.textLight }}>
              {t('cumulativePromotions.selected')} &middot; {value?.length ?? 0}
            </span>
            {!disabled && (
              <Stack gutterSize={0.5}>
                <ClearAllButton empty={isAllSelected} onClick={handleSelectAll}>
                  {t('cumulativePromotions.selectAll')}
                </ClearAllButton>
                <ClearAllButton
                  empty={(value?.length ?? 0) === 0}
                  onClick={handleRemoveAll}
                >
                  {t('cumulativePromotions.deselectAll')}
                </ClearAllButton>
              </Stack>
            )}
          </SummaryListHeader>
          {value?.length > 0 && (
            <DisplayListView>
              {value?.map((item) => (
                <DisplayListItem key={item?.id}>
                  <SelectionLabel>{item?.name?.toUpperCase()}</SelectionLabel>
                  <ButtonReset onClick={() => handleRemoveById(item?.id)}>
                    <VisuallyHidden>
                      {t('applicableRooms.deleteFromSelection', {
                        value: item?.name,
                      })}
                    </VisuallyHidden>
                    {!disabled && (
                      <i
                        className="fa-light fa-xmark"
                        style={{ fontSize: 24, color: theme.textLighter }}
                        aria-hidden="true"
                      />
                    )}
                  </ButtonReset>
                </DisplayListItem>
              ))}
            </DisplayListView>
          )}
        </>
      )}
    </Wrapper>
  );
};

const TriggerTextField = styled(Field)`
  width: 100%;
  border: 0px;
  &:focus {
    outline: none;
  }
  input:focus {
    outline: none;
  }
`;

const OrganizationDropdownToggle = ({ children, onChange }) => {
  const [value, setValue] = useState('');
  const handleChange = (e) => {
    setValue(e.target?.value);
    onChange(e.target?.value);
  };
  const handleOnClick = () => {
    if (!value) {
      onChange(' ');
    }
  };
  return (
    <TriggerTextField
      type="text"
      placeholder={children}
      value={value}
      onClick={handleOnClick}
      onChange={handleChange}
      data-testid={'trigger-text-field'}
    />
  );
};
export const OrganizationsField = ({ name, disabled, organizations }) => {
  const theme = useTheme();
  const [{ value }, organizationsMeta, { setValue }] = useField(name);
  const { t } = useTranslation('promotionForm');
  const { values } = useFormikContext();
  const isOperational = values?.state === PromotionStateEnum.OPERATIONAL;
  // TOs are required only when the offer does not apply to the DMC.
  const isRequired = isOperational && !values?.appliesToDmc;
  const hasError = isOperational
    ? Boolean(organizationsMeta.error)
    : Boolean(organizationsMeta.touched) && Boolean(organizationsMeta.error);

  const [valuesList, setValues] = useState(organizations);
  const handleValuesChange = (orgName) => {
    const newValues = organizations.filter((v) =>
      v.displayName.trim().toLowerCase().includes(orgName.toLowerCase().trim()),
    );

    if (!orgName || !orgName.trim() || newValues.length === 0) {
      setValues(organizations);
      return;
    }
    setValues(newValues);
  };

  const handleClick = (selectedElement) => {
    if (value?.length > 0) {
      if (value?.some(({ id }) => id === selectedElement.id)) {
        setValue(value.filter(({ id }) => id !== selectedElement.id));
      } else {
        setValue([...value, { ...selectedElement }]);
      }
    } else {
      setValue([{ ...selectedElement }]);
    }
  };

  const handleSelectAll = () => {
    setValue(
      (organizations ?? []).map(({ id, displayName }) => ({ id, displayName })),
    );
  };

  const handleRemoveAll = () => {
    setValue([]);
  };

  const handleRemoveById = (id) => {
    const nextState = value?.filter((item) => item.id !== id);
    setValue(nextState);
  };

  return (
    <Wrapper>
      <FormField>
        <FormLabel>
          {t('organizations.applicableOnTo')}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </FormLabel>
        {!disabled && (
          <Stack direction="column" gutterSize={1} size="full" alignX="stretch">
            <Dropdown
              trigger={
                <DropdownToggleWrapper>
                  <OrganizationDropdownToggle onChange={handleValuesChange}>
                    {t('organizations.searchToToAdd')}
                  </OrganizationDropdownToggle>
                </DropdownToggleWrapper>
              }
            >
              <PopoverContainer>
                <PopoverMenu>
                  {valuesList?.map(({ id, displayName }) => (
                    <PopoverItemButton
                      key={id}
                      onClick={() => handleClick({ id, displayName })}
                      type="button"
                      data-testid={`select-${displayName
                        ?.split(' ')
                        .join('-')
                        .toLowerCase()}`}
                    >
                      <Stack
                        size="full"
                        alignX="space-between"
                        alignY="center"
                        gutterSize={0.5}
                      >
                        <Stack alignY="center" gutterSize={0.25}>
                          <Text type="body">
                            <SelectionLabel>{displayName}</SelectionLabel>
                          </Text>
                        </Stack>
                        {value?.some((element) => element.id === id) ? (
                          <i
                            className="fa-regular fa-circle-check"
                            style={{ fontSize: 24, color: theme.primary }}
                          />
                        ) : (
                          <i
                            className="fa-regular fa-circle-plus"
                            style={{ fontSize: 24, color: theme.textLight }}
                          />
                        )}
                      </Stack>
                    </PopoverItemButton>
                  ))}
                </PopoverMenu>
              </PopoverContainer>
            </Dropdown>
          </Stack>
        )}
      </FormField>
      {hasError && (
        <ErrorMessageWrapper style={{ marginTop: theme.spacing(0.25) }}>
          {t('recipients.required')}
        </ErrorMessageWrapper>
      )}
      <SummaryListHeader>
        <span style={{ color: theme.textLight }}>
          {t('organizations.toSelected')} &middot; {value?.length ?? 0}
        </span>
        {!disabled && (
          <Stack gutterSize={0.5}>
            <ClearAllButton
              empty={(value?.length ?? 0) === organizations?.length}
              onClick={handleSelectAll}
            >
              {t('organizations.selectAll')}
            </ClearAllButton>
            <ClearAllButton
              empty={(value?.length ?? 0) === 0}
              onClick={handleRemoveAll}
            >
              {t('organizations.deselectAll')}
            </ClearAllButton>
          </Stack>
        )}
      </SummaryListHeader>
      {value?.length > 0 && (
        <DisplayListView>
          {value?.map((item) => (
            <DisplayListItem key={item?.id}>
              <SelectionLabel>
                {item?.displayName?.toUpperCase()}
              </SelectionLabel>
              <ButtonReset onClick={() => handleRemoveById(item?.id)}>
                <VisuallyHidden>
                  {t('applicableTo.deleteFromSelection', {
                    value: item?.displayName,
                  })}
                </VisuallyHidden>
                {!disabled && (
                  <i
                    className="fa-light fa-xmark"
                    style={{ fontSize: 24, color: theme.textLighter }}
                    aria-hidden="true"
                  />
                )}
              </ButtonReset>
            </DisplayListItem>
          ))}
        </DisplayListView>
      )}
    </Wrapper>
  );
};
const PromotionBookingPeriodsModal = ({ open, setOpen, index }) => {
  const { t } = useTranslation('promotionForm');
  const [field, meta] = useField(`periods[${index}]`);
  const { values } = useFormikContext();
  const isOperational = values?.state === PromotionStateEnum.OPERATIONAL;
  const period = field?.value;
  const overflowValue =
    period && period?.bookingPeriods?.length >= 2 ? 'auto' : 'visible';
  const isDesabled = Boolean(meta.error || meta.touched);

  return (
    <KoobModal
      open={open}
      setOpen={setOpen}
      widthClass="sm:max-w-xl xl:max-w-2xl overflow-visible"
    >
      <div className={`max-h-[600px] overflow-y-${overflowValue}`}>
        <Text type="h3" className="text-center mb-2">
          {t('bookingPeriods.label')}
        </Text>
        <div className="w-full flex space-x-3 items-center my-2 py-2">
          <div className="font-medium text-lg">
            {t('bookingPeriods.validityPeriod')}
          </div>
          <KoobBadge>
            {formatDate(period?.startAt, 'DD/MM/YYYY')}
            <i className="fa fa-arrow-right mx-1.5" />
            {formatDate(period?.endAt, 'DD/MM/YYYY')}
          </KoobBadge>
        </div>
        <PeriodsField
          name={`periods[${index}].bookingPeriods`}
          canRemoveLastElement
          required={isOperational}
        />
      </div>
      <div className="w-full mt-5 flex justify-end">
        <KoobButton
          type="button"
          variant="primary"
          onClick={() => setOpen(false)}
          disabled={isDesabled}
        >
          {t('misc:save')}
        </KoobButton>
      </div>
    </KoobModal>
  );
};

const PromotionBookingPeriods = ({ index }) => {
  const { t } = useTranslation('promotionForm');
  const [showBookingPeriodsModal, setShowBookingPeriodsModal] = useState(false);
  const [field] = useField(`periods[${index}]`);
  const period = field?.value;
  const isDisabled = isEmpty(period?.startAt) || isEmpty(period?.endAt);
  const periodBookingCounter = period?.bookingPeriods?.length;

  return (
    <>
      <Stack size="full" alignY="center" className="my-3">
        <span style={{ flex: '0 0 auto' }}>{t('bookingPeriods.title')}</span>
        <Stack
          size="full"
          alignX="end"
          alignY="center"
          gutterSize={1}
          className="w-full"
        >
          <span>
            {t('bookingPeriods.configured', { count: periodBookingCounter })}
          </span>
          <KoobButton
            variant="primary"
            type="button"
            onClick={() => setShowBookingPeriodsModal(true)}
            disabled={isDisabled}
          >
            {t('bookingPeriods.label')}
          </KoobButton>
        </Stack>
      </Stack>
      <PromotionBookingPeriodsModal
        open={showBookingPeriodsModal}
        setOpen={setShowBookingPeriodsModal}
        index={index}
      />
    </>
  );
};

const PromotionForm = ({
  promotion,
  style,
  cancellationPolicies,
  setUnsavedData,
  promotionField,
  isDuplicate,
  toOrganizations = [],
  values,
  isSubmitting,
  touched,
  isValid,
  dirty,
  dealTypeSelectionAllowed,
  promotionsThatCanBeLinked,
  hasBookingPeriod,
  hasCalculationOrder = false,
  validateForm,
  isReadOnly = false,
}) => {
  const { t } = useTranslation('promotionForm');
  const theme = useTheme();
  const { setFieldValue } = useFormikContext();
  const stateOptions = [
    { value: 'draft', label: <TextPadding>{t('states.draft')}</TextPadding> },
    {
      value: 'operational',
      label: <TextPadding>{t('states.operational')}</TextPadding>,
    },
    {
      value: 'archived',
      label: <TextPadding>{t('states.archived')}</TextPadding>,
    },
  ];
  const selectedPromotionType =
    values?.params?.type?.value ?? values?.params?.type;
  const isStayFlexibilityAvailable =
    !selectedPromotionType || isSupplementPromotionType(selectedPromotionType);

  const dealOptions = [
    {
      value: Deals.basic_deal,
      label: <Deal displayName={t('deals.basicDeal')} icon="fa-tag" />,
    },
    {
      value: Deals.early_booker,
      label: (
        <Deal displayName={t('deals.earlyBooker')} icon="fa-alarm-clock" />
      ),
    },
    {
      value: Deals.last_minute,
      label: <Deal displayName={t('deals.lastMinute')} icon="fa-stopwatch" />,
    },
    ...(isStayFlexibilityAvailable
      ? [
          {
            value: Deals.stay_flexibility,
            label: (
              <Deal
                displayName={t('deals.stayFlexibility')}
                icon="fa-calendar"
              />
            ),
          },
        ]
      : []),
  ];

  const stayFlexibilityOptions = [
    {
      value: StayFlexibleCodeEnum.EARLY_CHECK_IN,
      label: (
        <TextPadding>{t('stayFlexibility.options.earlyCheckIn')}</TextPadding>
      ),
    },
    {
      value: StayFlexibleCodeEnum.LATE_CHECK_OUT,
      label: (
        <TextPadding>{t('stayFlexibility.options.lateCheckOut')}</TextPadding>
      ),
    },
  ];

  useEffect(() => {
    if (
      isStayFlexibilityKind(values?.kind) &&
      selectedPromotionType &&
      !isSupplementPromotionType(selectedPromotionType)
    ) {
      setFieldValue('kind', Deals.basic_deal);
      setFieldValue('stayFlexibleCode', undefined);
    }
  }, [selectedPromotionType, setFieldValue, values?.kind]);

  var LinkCancel = '..';
  var isDuplicated = false;
  var disabledButton = false;
  if (isDuplicate) {
    isDuplicated = true;
    LinkCancel = '../..';
  } else if (promotion?.state === 'archived') {
    isDuplicated = false;
    disabledButton = true;
  }
  disabledButton = disabledButton || isReadOnly;

  return (
    <Form style={style}>
      <FormNavigationBlocker />
      <Stack direction="column" gutterSize={1} size="full" alignX="stretch">
        <Grid style={{ gridTemplateAreas: style?.gridTemplateAreas }}>
          <KoobCard style={{ gridArea: 'top' }}>
            <Stack
              gutterSize={4}
              size="full"
              alignY="flex-end"
              alignX="space-between"
            >
              <div style={{ width: 'calc(50% - 32px)' }}>
                <TextField
                  label={t('promotionName')}
                  name="name"
                  required
                  disabled={isReadOnly}
                  touchRequired={
                    values?.state === PromotionStateEnum.OPERATIONAL
                      ? false
                      : undefined
                  }
                />
              </div>
              {isDuplicated ||
              promotion?.state !== PromotionStateEnum.ARCHIVED ? (
                <SegmentedControlField
                  name="state"
                  options={stateOptions}
                  style={{ display: 'flex' }}
                  disabled={isReadOnly}
                  onChange={(value) => {
                    if (value === PromotionStateEnum.OPERATIONAL) {
                      setTimeout(() => validateForm(), 0);
                    }
                  }}
                />
              ) : null}
            </Stack>
          </KoobCard>
          {dealTypeSelectionAllowed && (
            <Cell gridArea="deal">
              <Text type="h2">{t('subTitles.offerType')}</Text>
              <KoobCard>
                <Stack gutterSize={0.5} direction="column" size="full">
                  <Stack size="full" alignX="center">
                    {/* 3. Pass the custom callback safely here */}
                    <SegmentedControlField
                      name="kind"
                      options={dealOptions}
                      onChange={(newKind) => {
                        if (
                          newKind === Deals.stay_flexibility &&
                          selectedPromotionType &&
                          !isSupplementPromotionType(selectedPromotionType)
                        ) {
                          setFieldValue('kind', Deals.basic_deal);
                          setFieldValue('stayFlexibleCode', undefined);
                          return;
                        }

                        if (isStayFlexibilityKind(newKind)) {
                          setFieldValue(
                            'stayFlexibleCode',
                            values?.stayFlexibleCode ??
                              StayFlexibleCodeEnum.EARLY_CHECK_IN,
                          );
                        } else {
                          setFieldValue('stayFlexibleCode', undefined);
                        }

                        if (newKind === PromotionKindEnum.BASIC_DEAL) {
                          setFieldValue(
                            'numberOfNights',
                            values?.numberOfNights || 1,
                          );
                        } else {
                          setFieldValue(
                            'conditionalDaysCount',
                            values?.conditionalDaysCount || 1,
                          );
                        }
                      }}
                    />
                  </Stack>
                  <Stack
                    direction="row"
                    alignY="center"
                    style={{ paddingBlock: theme.spacing(0.5) }}
                  >
                    {!isStayFlexibilityKind(values?.kind) && (
                      <PromotionsNightsNumberField
                        name={
                          values?.kind === PromotionKindEnum.BASIC_DEAL
                            ? 'numberOfNights'
                            : 'conditionalDaysCount'
                        }
                        kind={values?.kind}
                        required={
                          values?.state === PromotionStateEnum.OPERATIONAL
                        }
                        touchRequired={
                          values?.state === PromotionStateEnum.OPERATIONAL
                            ? false
                            : undefined
                        }
                      />
                    )}
                  </Stack>
                  {[
                    PromotionKindEnum.EARLY_BOOKER,
                    PromotionKindEnum.LAST_MINUTE,
                  ].includes(values?.kind) && (
                    <Stack
                      direction="row"
                      alignY="center"
                      style={{ paddingBlock: theme.spacing(0.5) }}
                    >
                      <PromotionsNightsNumberField
                        name="numberOfNights"
                        kind={PromotionKindEnum.BASIC_DEAL}
                      />
                    </Stack>
                  )}
                  {isStayFlexibilityKind(values?.kind) && (
                    <Stack
                      direction="column"
                      alignX="stretch"
                      style={{ width: '100%' }}
                    >
                      <FormLabel variant="bare">
                        {t('stayFlexibility.label')}
                      </FormLabel>
                      <SegmentedControlField
                        name="stayFlexibleCode"
                        options={stayFlexibilityOptions}
                        required={
                          values?.state === PromotionStateEnum.OPERATIONAL
                        }
                        touchRequired={
                          values?.state === PromotionStateEnum.OPERATIONAL
                            ? false
                            : undefined
                        }
                      />
                    </Stack>
                  )}
                  <Divider />
                  <InlineFormField style={{ alignSelf: 'stretch' }}>
                    <FormLabel variant="bare">
                      {t('minRoomsToBook')}
                      {values?.state === PromotionStateEnum.OPERATIONAL && (
                        <span className="ml-1 text-red-500">*</span>
                      )}
                    </FormLabel>
                    <Stack style={{ marginLeft: 'auto' }}>
                      <KoobInputStepperField
                        name="minRooms"
                        min={1}
                        required={
                          values?.state === PromotionStateEnum.OPERATIONAL
                        }
                        touchRequired={
                          values?.state === PromotionStateEnum.OPERATIONAL
                            ? false
                            : undefined
                        }
                      />
                    </Stack>
                  </InlineFormField>
                </Stack>
              </KoobCard>
            </Cell>
          )}

          <Cell gridArea="period">
            <Text type="h2">{t('subTitles.period')}</Text>
            <KoobCard>
              <div style={{ width: '500px' }} />
              <PeriodsField
                required={values?.state === PromotionStateEnum.OPERATIONAL}
                customPeriodFields={hasBookingPeriod && PromotionBookingPeriods}
                name="periods"
                disabled={isReadOnly}
              />
            </KoobCard>
          </Cell>

          <Cell gridArea="promotionParams">
            <Text type="h2">{t('subTitles.promotionParams')}</Text>
            <KoobCard>
              {promotionField}
              <>
                <Divider />

                <CumulativePromotionsField
                  name="cumulativePromotions"
                  promotions={promotionsThatCanBeLinked}
                  disabled={isReadOnly}
                  hasCalculationOrder={hasCalculationOrder}
                  isDuplicate={isDuplicate}
                />
              </>
              <Divider />
              <Stack direction="column" gutterSize={0.5} size="full">
                <FormLabel variant="bare">{t('recipients.label')}</FormLabel>
                <Text type="bodySmall" color="textLighter">
                  {t('recipients.sharedConfiguration')}
                </Text>
                <KoobInputCheckboxField
                  name="appliesToDmc"
                  label={t('recipients.dmc')}
                  disabled={isReadOnly}
                />
              </Stack>
              <OrganizationsField
                name="organizations"
                organizations={toOrganizations}
                disabled={isReadOnly}
              />
            </KoobCard>
          </Cell>
          {cancellationPolicies}
        </Grid>
        <Stack gutterSize={0.5}>
          <KoobButton
            style={{ marginLeft: 'auto' }}
            variant="secondary"
            type="button"
            asChild
          >
            <Link relative="path" to={LinkCancel}>
              {t('misc:cancel')}
            </Link>
          </KoobButton>
          {isDuplicated ? (
            <KoobButton
              type="submit"
              variant="primary"
              loading={isSubmitting}
              disabled={!isValid || disabledButton}
              testId="save-promotion"
            >
              {t('misc:save')}
            </KoobButton>
          ) : (
            <KoobButton
              type="submit"
              disabled={!dirty || !isValid || disabledButton}
              variant="primary"
              loading={isSubmitting}
              testId="save-promotion"
            >
              {t('misc:save')}
            </KoobButton>
          )}
        </Stack>
      </Stack>
      {setUnsavedData && (
        <UnsavedFormTrigger
          dirty={dirty && touched}
          setIsUnsaved={setUnsavedData}
        />
      )}
    </Form>
  );
};

export default PromotionForm;
